import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  FolderKanban,
  Users,
  AlertTriangle,
  ArrowRight,
  Clock,
  Target,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Lightbulb,
  CalendarClock,
} from 'lucide-react';

import { SensitiveData } from '@/components/sensitive-data';
import { KpiCard } from '@/components/kpi-card';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import {
  fetchProjects,
  fetchPortfolioSummary,
  fetchUtilisationData,
  fetchClients,
  fetchEmployees,
} from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';
import {
  formatCurrency,
  formatPercent,
  getHealthBadgeColor,
  formatStatus,
  getVarianceColor,
} from '@/lib/utils';
import { getProjectStatusColor } from '@/lib/status-colors';
import type { Project, PortfolioSummary, UtilisationData, Client, Employee } from '@/types';
import { PROJECT_STATUS_LABELS } from '@/types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import {
  mockSchedulerFeatureFlag,
  mockDashboardAlerts,
  mockProjectBookingHealth,
  type DashboardSchedulingAlert,
  type ProjectBookingHealth,
} from '@/lib/mock-scheduling-data';
import { useSchedulerConnection } from '@/hooks/use-scheduler-connection';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

export function DashboardPage({ onNavigate }: { onNavigate?: (page: string) => void } = {}) {
  const { statusLabels } = useProjectStatuses();
  const { isConnected: schedulerConnected } = useSchedulerConnection();
  const navigateHook = useNavigate();
  const handleNavigate = onNavigate || ((page: string) => navigateHook(`/${page}`));
  const { currentCompany } = useCompany();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [utilisation, setUtilisation] = useState<UtilisationData[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const { handleApiError } = useErrorHandler({ useToast: true });

  const handleApiErrorRef = useRef(handleApiError);
  handleApiErrorRef.current = handleApiError;

  useEffect(() => {
    let ignore = false;

    async function loadData() {
      setLoading(true);
      const results = await Promise.allSettled([
        fetchProjects('dashboard', { page: 1, pageSize: 10 }, { statusId: 2, sortBy: 'health' }),
        fetchPortfolioSummary('dashboard'),
        fetchUtilisationData('dashboard'),
        fetchClients('dashboard'),
        fetchEmployees('dashboard'),
      ]);

      if (ignore) return;

      const [projectsResult, portfolioResult, utilisationResult, clientsResult, employeesResult] =
        results;

      if (projectsResult.status === 'fulfilled') {
        const val = projectsResult.value;
        setProjects(Array.isArray(val) ? val : (val?.data ?? []));
      } else {
        handleApiErrorRef.current(projectsResult.reason, '/api/projects', 'GET');
      }

      if (portfolioResult.status === 'fulfilled') {
        setPortfolio(portfolioResult.value);
      } else {
        handleApiErrorRef.current(portfolioResult.reason, '/api/portfolio', 'GET');
      }

      if (utilisationResult.status === 'fulfilled') {
        setUtilisation(utilisationResult.value);
      } else {
        handleApiErrorRef.current(utilisationResult.reason, '/api/utilisation', 'GET');
      }

      if (clientsResult.status === 'fulfilled') {
        setClients(clientsResult.value);
      } else {
        handleApiErrorRef.current(clientsResult.reason, '/api/clients', 'GET');
      }

      if (employeesResult.status === 'fulfilled') {
        setEmployees(employeesResult.value);
      } else {
        handleApiErrorRef.current(employeesResult.reason, '/api/employees', 'GET');
      }

      setLoading(false);
    }
    loadData();

    return () => {
      ignore = true;
    };
  }, [currentCompany?.id]);

  const activeProjects = projects.filter(p => p.statusId === 2);
  const atRiskProjects = activeProjects.filter(p => p.health === 'amber' || p.health === 'red');
  const avgUtilisation =
    utilisation.reduce((sum, u) => sum + u.utilisationPercent, 0) / utilisation.length || 0;

  // Calculate portfolio profitability
  const totalRevenue = activeProjects.reduce((sum, p) => sum + (p.actualRevenue || 0), 0);
  const totalCosts = activeProjects.reduce((sum, p) => sum + ((p.actualLabour || 0) + (p.actualExpense || 0)), 0);
  const totalProfit = totalRevenue - totalCosts;
  const portfolioMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  // Calculate margin trend (simulated 6-month trend)
  const marginTrendData = [
    { month: 'Aug', margin: 21.2 },
    { month: 'Sep', margin: 22.1 },
    { month: 'Oct', margin: 23.5 },
    { month: 'Nov', margin: 22.8 },
    { month: 'Dec', margin: 23.9 },
    { month: 'Jan', margin: portfolioMargin },
  ];

  // Project health distribution
  const greenProjects = activeProjects.filter(p => p.health === 'green').length;
  const amberProjects = activeProjects.filter(p => p.health === 'amber').length;
  const redProjects = activeProjects.filter(p => p.health === 'red').length;

  const healthDistribution = [
    { name: 'Healthy', value: greenProjects, color: '#10b981' },
    { name: 'At Risk', value: amberProjects, color: '#f59e0b' },
    { name: 'Critical', value: redProjects, color: '#ef4444' },
  ];

  // Revenue vs Cost comparison (top 5 projects by revenue)
  const revenueVsCostData = activeProjects
    .sort((a, b) => (b.actualRevenue || 0) - (a.actualRevenue || 0))
    .slice(0, 5)
    .map(p => ({
      name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name,
      revenue: p.actualRevenue || 0,
      cost: (p.actualLabour || 0) + (p.actualExpense || 0),
      profit: (p.actualRevenue || 0) - ((p.actualLabour || 0) + (p.actualExpense || 0)),
    }));

  // Top/Bottom profitable projects
  const projectsWithProfit = activeProjects.map(p => ({
    ...p,
    profit: (p.actualRevenue || 0) - ((p.actualLabour || 0) + (p.actualExpense || 0)),
  }));

  const topProfitable = [...projectsWithProfit]
    .sort((a, b) => b.profit - a.profit)
    .slice(0, 3);

  const bottomProfitable = [...projectsWithProfit]
    .sort((a, b) => a.profit - b.profit)
    .slice(0, 3);

  // Enhanced at-risk analysis with root causes
  const atRiskWithInsights = atRiskProjects.map(project => {
    const insights: { type: string; severity: string; message: string }[] = [];
    const recommendations: string[] = [];

    // Budget variance analysis
    if (project.costVariancePercent < -5) {
      insights.push({
        type: 'budget',
        severity: project.costVariancePercent < -10 ? 'critical' : 'warning',
        message: `${formatPercent(Math.abs(project.costVariancePercent))} over cost budget (${formatCurrency(Math.abs(project.costVariance))})`,
      });

      if (project.hoursVariancePercent > 5) {
        insights.push({
          type: 'hours',
          severity: 'warning',
          message: `Labour hours ${formatPercent(project.hoursVariancePercent)} over plan`,
        });
        recommendations.push('Reduce team size by 1 FTE or renegotiate scope');
      }
    }

    // Margin analysis
    if (project.marginPercent < 15) {
      insights.push({
        type: 'margin',
        severity: project.marginPercent < 10 ? 'critical' : 'warning',
        message: `Low margin: ${formatPercent(project.marginPercent)} (target: 30%)`,
      });
      recommendations.push('Increase billing rate or reduce costs');
    }

    // Timeline analysis
    const expectedCompletion = project.completionPercent;
    const startDate = new Date(project.startDate);
    const endDate = new Date(project.endDate);
    const today = new Date();
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = today.getTime() - startDate.getTime();
    const expectedProgress = (elapsed / totalDuration) * 100;

    if (expectedCompletion < expectedProgress - 10) {
      insights.push({
        type: 'timeline',
        severity: 'warning',
        message: `Behind schedule: ${formatPercent(expectedCompletion)} complete vs ${formatPercent(expectedProgress)} expected`,
      });
      recommendations.push('Review project plan and adjust timeline');
    }

    // WIP analysis
    if (project.wipAmount > 50000) {
      insights.push({
        type: 'wip',
        severity: 'warning',
        message: `High WIP: ${formatCurrency(project.wipAmount)} unbilled`,
      });
      recommendations.push('Accelerate invoicing to improve cash flow');
    }

    return {
      ...project,
      insights,
      recommendations: recommendations.slice(0, 2), // Top 2 recommendations
    };
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="transition-all hover:shadow-md">
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="Homepage"
        subtitle="Overview of all projects and key metrics"
        size={20}
        showActions={false}
      />

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Active Projects"
          value={portfolio?.activeProjects || 0}
          icon={FolderKanban}
          description={`${atRiskProjects.length} at risk`}
        />

        <KpiCard
          title="Portfolio Value"
          value={
            <SensitiveData>
              {formatCurrency(portfolio?.portfolioValue || 0)}
            </SensitiveData>
          }
          icon={DollarSign}
          description="Total contract value"
        />

        <KpiCard
          title="Avg Margin"
          value={
            <SensitiveData>
              {formatPercent(portfolio?.avgMarginPercent || 0)}
            </SensitiveData>
          }
          icon={TrendingUp}
          trend={{
            value: 2.5,
            label: '%',
            isPositive: true,
          }}
          description="Above target"
        />

        <KpiCard
          title="Team Utilisation"
          value={formatPercent(avgUtilisation, 0)}
          icon={Users}
          description="Target: 75%"
          trend={{
            value: avgUtilisation - 75,
            label: '%',
            isPositive: avgUtilisation >= 75,
          }}
        />
      </div>

      {/* Aged WIP Alert - MOVED UP for maximum demo impact */}
      {(() => {
        // Calculate aged WIP
        const wipProjects = activeProjects.filter(p => p.wipAmount > 0);
        const totalWIP = wipProjects.reduce((sum, p) => sum + p.wipAmount, 0);
        
        // Simulate aging buckets (in real app, this would come from backend with actual dates)
        // For demo: assume 20% is 90+ days, 20% is 61-90 days, 30% is 31-60 days, 30% is 1-30 days
        const aged90Plus = totalWIP * 0.2;
        const aged61to90 = totalWIP * 0.2;
        const aged30to60 = totalWIP * 0.3;
        const aged0to30 = totalWIP * 0.3;
        const agedOver60 = aged61to90 + aged90Plus;
        
        // Show alert if aged WIP over 60 days exceeds £25k (lowered for demo visibility)
        const hasAgedWIP = agedOver60 > 25000;
        
        if (!hasAgedWIP) return null;
        
        return (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-5 w-5 text-destructive" />
                Aged WIP Alert - Cash Flow Risk
              </CardTitle>
              <CardDescription>Work completed but not yet invoiced</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                <div>
                  <p className="text-sm text-muted-foreground">WIP Over 60 Days</p>
                  <p className="text-2xl font-bold text-destructive">
                    <SensitiveData>{formatCurrency(agedOver60)}</SensitiveData>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    This is cash that should be in your bank account
                  </p>
                </div>
                <AlertCircle className="h-12 w-12 text-destructive opacity-20" />
              </div>

              <div className="grid gap-3 md:grid-cols-4">
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">1–30 Days</span>
                    <span className="text-xs font-medium text-success-600">
                      <SensitiveData>{formatCurrency(aged0to30)}</SensitiveData>
                    </span>
                  </div>
                  <Progress value={75} className="h-2" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">31–60 Days</span>
                    <span className="text-xs font-medium text-warning-600">
                      <SensitiveData>{formatCurrency(aged30to60)}</SensitiveData>
                    </span>
                  </div>
                  <Progress value={50} className="h-2" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">61–90 Days</span>
                    <span className="text-xs font-medium text-orange-500">
                      <SensitiveData>{formatCurrency(aged61to90)}</SensitiveData>
                    </span>
                  </div>
                  <Progress value={35} className="h-2" />
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">90+ Days</span>
                    <span className="text-xs font-medium text-destructive">
                      <SensitiveData>{formatCurrency(aged90Plus)}</SensitiveData>
                    </span>
                  </div>
                  <Progress value={20} className="h-2" />
                </div>
              </div>

              <div className="bg-background rounded-lg p-3 border">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-warning-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">Recommended Actions:</p>
                    <ul className="text-xs text-muted-foreground mt-1 space-y-1">
                      <li>• Review projects with WIP over 60 days and accelerate invoicing</li>
                      <li>• Implement weekly WIP review meetings with project managers</li>
                      <li>• Set up automated alerts when WIP exceeds 30 days</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Total WIP: <SensitiveData>{formatCurrency(totalWIP)}</SensitiveData>
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleNavigate('wip-reporting')}
                >
                  View WIP Report
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Resourcing Alerts Panel — only shown when scheduler is connected */}
      {schedulerConnected && (() => {
        const alerts = mockDashboardAlerts.slice(0, 5);
        const criticalCount = alerts.filter(a => a.severity === 'critical').length;
        const warningCount  = alerts.filter(a => a.severity === 'warning').length;

        return (
          <Card className="border-orange-200 bg-orange-50 dark:border-orange-900 dark:bg-orange-950/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                  Resourcing Alerts
                </CardTitle>
                <div className="flex items-center gap-2">
                  {criticalCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 dark:bg-red-900/40 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-500 inline-block" />
                      {criticalCount} critical
                    </span>
                  )}
                  {warningCount > 0 && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 inline-block" />
                      {warningCount} warning
                    </span>
                  )}
                </div>
              </div>
              <CardDescription>
                Projects with booking health issues requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-1 pb-4">
              {alerts.map((alert: DashboardSchedulingAlert) => {
                const isCritical = alert.severity === 'critical';
                return (
                  <button
                    key={alert.projectId}
                    onClick={() => handleNavigate(`project-details/${alert.projectId}`)}
                    className="w-full text-left rounded-lg border border-border bg-card dark:bg-card px-4 py-3 hover:bg-accent/50 transition-colors group"
                  >
                    <div className="flex items-start gap-3">
                      {/* Severity dot */}
                      <span className="mt-1 flex-shrink-0">
                        {isCritical ? (
                          <span className="flex h-3 w-3 items-center justify-center">
                            <span className="h-3 w-3 rounded-full bg-red-500" />
                          </span>
                        ) : (
                          <span className="flex h-3 w-3 items-center justify-center">
                            <span className="h-3 w-3 rounded-full bg-amber-400" />
                          </span>
                        )}
                      </span>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm truncate group-hover:text-primary transition-colors">
                            {alert.projectName}
                          </span>
                          <span className={`text-xs flex-shrink-0 ${isCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                            {alert.primaryAlertMessage}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          PM: {alert.pmName}
                        </p>
                      </div>

                      {/* Chevron */}
                      <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                );
              })}

              {/* Footer */}
              <div className="flex justify-end pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleNavigate('projects')}
                >
                  View All Projects
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Financial Summary Card */}
      <Card className="border-2 border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Portfolio Profitability
          </CardTitle>
          <CardDescription>Real-time profit and margin analysis</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Profit</p>
              <p className="text-2xl font-bold">
                <SensitiveData>
                  {formatCurrency(totalProfit)}
                </SensitiveData>
              </p>
              <p className={`text-xs flex items-center gap-1 ${totalProfit >= 0 ? 'text-success-600' : 'text-destructive'}`}>
                {totalProfit >= 0 ? (
                  <>
                    <TrendingUp className="h-3 w-3" />
                    Profitable
                  </>
                ) : (
                  <>
                    <TrendingDown className="h-3 w-3" />
                    Loss
                  </>
                )}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Portfolio Margin</p>
              <p className="text-2xl font-bold">
                <SensitiveData>
                  {formatPercent(portfolioMargin)}
                </SensitiveData>
              </p>
              <p className={`text-xs flex items-center gap-1 ${portfolioMargin >= 25 ? 'text-success-600' : 'text-warning-600'}`}>
                Target: 30%
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Projects at Risk</p>
              <p className="text-2xl font-bold text-warning-600">
                {atRiskProjects.length}
              </p>
              <p className="text-xs text-muted-foreground">
                {redProjects} critical, {amberProjects} warning
              </p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-medium text-success-600 flex items-center gap-1">
                <TrendingUp className="h-4 w-4" />
                Top Profitable Projects
              </p>
              <div className="space-y-1">
                {topProfitable.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span className="truncate flex-1">{p.name}</span>
                    <SensitiveData>
                      <span className="font-medium text-success-600">
                        {formatCurrency(p.profit)}
                      </span>
                    </SensitiveData>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-destructive flex items-center gap-1">
                <TrendingDown className="h-4 w-4" />
                Least Profitable Projects
              </p>
              <div className="space-y-1">
                {bottomProfitable.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span className="truncate flex-1">{p.name}</span>
                    <SensitiveData>
                      <span className={`font-medium ${p.profit < 0 ? 'text-destructive' : 'text-muted-foreground'}`}>
                        {formatCurrency(p.profit)}
                      </span>
                    </SensitiveData>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Enhanced At Risk Projects with Intelligence */}
      {atRiskWithInsights.length > 0 && (
        <Card className="border-warning-200 bg-warning-50 dark:bg-warning-950/20">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning-600" />
              {atRiskWithInsights.length} Project{atRiskWithInsights.length > 1 ? 's' : ''} Need Attention
            </CardTitle>
            <CardDescription>
              Projects with budget overruns, timeline delays, or low margins
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {atRiskWithInsights.slice(0, 3).map(project => {
                const firstClientId = project.clientIds?.split(',')[0];
                const client = firstClientId ? clients.find(c => c.id === Number(firstClientId)) : undefined;
                return (
                  <div
                    key={project.id}
                    className="p-4 bg-card dark:bg-card rounded-lg border border-border space-y-3"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Badge className={getHealthBadgeColor(project.health)}>
                            {project.health.toUpperCase()}
                          </Badge>
                          <span className="font-medium">{project.name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {client?.name} • {formatPercent(project.completionPercent, 0)} complete
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleNavigate(`project-details/${project.id}`)}
                      >
                        View Details <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>

                    {/* Root Cause Insights */}
                    {project.insights && project.insights.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Issues Detected:
                        </p>
                        <div className="space-y-1">
                          {project.insights.map((insight, idx) => (
                            <div 
                              key={idx}
                              className={`text-xs p-2 rounded flex items-start gap-2 ${
                                insight.severity === 'critical' 
                                  ? 'bg-destructive/10 text-destructive' 
                                  : 'bg-warning/10 text-warning-700 dark:text-warning-400'
                              }`}
                            >
                              {insight.severity === 'critical' ? (
                                <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              ) : (
                                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              )}
                              <span>{insight.message}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {project.recommendations && project.recommendations.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Lightbulb className="h-3 w-3" />
                          Recommended Actions:
                        </p>
                        <div className="space-y-1">
                          {project.recommendations.map((rec, idx) => (
                            <div 
                              key={idx}
                              className="text-xs p-2 rounded bg-info/10 text-info-700 dark:text-info-400 flex items-start gap-2"
                            >
                              <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0" />
                              <span>{rec}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Visual Analytics Charts - MOVED UP for visual impact */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* Margin Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Margin Trend
            </CardTitle>
            <CardDescription>Portfolio margin over last 6 months</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={marginTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  fontSize={12}
                />
                <YAxis 
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  fontSize={12}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                  formatter={(value: number) => [`${value.toFixed(1)}%`, 'Margin']}
                />
                <Line 
                  type="monotone" 
                  dataKey="margin" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: 'hsl(var(--primary))', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Project Health Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              Project Health
            </CardTitle>
            <CardDescription>Distribution across {activeProjects.length} active projects</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={healthDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {healthDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry: any) => `${value}: ${entry.payload.value}`}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Revenue vs Cost */}
        <Card className="md:col-span-2 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Revenue vs Cost
            </CardTitle>
            <CardDescription>Top 5 projects by revenue</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={revenueVsCostData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  fontSize={10}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  fontSize={12}
                  tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                  }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Legend />
                <Bar dataKey="revenue" fill="#22c55e" name="Revenue" />
                <Bar dataKey="cost" fill="#f97316" name="Cost" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Project Health Table - SHORTENED to 5 projects */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Project Health</CardTitle>
            <Button variant="outline" size="sm" onClick={() => handleNavigate('projects')}>
              View All Projects
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
              {activeProjects.slice(0, 5).map((project, idx) => {
                // Get client name - use clientNames if available, otherwise look up first client
                const clientName = project.clientNames || (project.clientIds?.split(',')[0] ? clients.find(c => c.id === Number(project.clientIds?.split(',')[0]))?.name : undefined);
                const manager = employees.find(e => e.id === project.projectManagerId);

                // Booking health — match by index for mock data (real API will key by project.id)
                const bookingHealth: ProjectBookingHealth | undefined =
                  schedulerConnected
                    ? mockProjectBookingHealth[idx]
                    : undefined;

                // Derive coverage colour band
                const coveragePct = bookingHealth?.coveragePercent ?? null;
                const coverageBarColor =
                  coveragePct === null ? 'bg-muted'
                  : coveragePct >= 95 ? 'bg-green-500'
                  : coveragePct >= 50 ? 'bg-amber-400'
                  : 'bg-red-500';
                const coverageTextColor =
                  coveragePct === null ? 'text-muted-foreground'
                  : coveragePct >= 95 ? 'text-green-600'
                  : coveragePct >= 50 ? 'text-amber-600'
                  : 'text-red-600';

              return (
                <div key={project.id} className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge className={getHealthBadgeColor(project.health)}>
                          {project.health.toUpperCase()}
                        </Badge>
                        <span className="font-medium">{project.name}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span>{clientName}</span>
                        <span>•</span>
                        <span>{manager?.name}</span>
                        <span>•</span>
                        <Badge variant="outline" className={getProjectStatusColor(project.statusId)}>
                          {statusLabels[project.statusId] || PROJECT_STATUS_LABELS[project.statusId] || formatStatus(String(project.statusId))}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="text-sm font-medium">
                        <SensitiveData>
                          {formatCurrency(project.budgetRevenue)}
                        </SensitiveData>
                      </div>
                      <div className={`text-xs ${getVarianceColor(project.costVariancePercent)}`}>
                        {project.costVariancePercent > 0 ? '+' : ''}
                        {formatPercent(project.costVariancePercent)} cost variance
                      </div>
                    </div>
                  </div>

                  {/* Completion progress bar */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <Progress value={project.completionPercent} className="h-2" />
                    </div>
                    <span className="text-xs text-muted-foreground w-12 text-right">
                      {formatPercent(project.completionPercent, 0)}
                    </span>
                  </div>

                  {/* Booking health — only when scheduler is connected */}
                  {bookingHealth && (
                    <div className="flex items-center gap-3 pt-0.5">
                      {/* Booking Coverage mini progress bar */}
                      <div className="flex-1 flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${coverageBarColor}`}
                            style={{ width: `${coveragePct ?? 0}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium w-12 text-right ${coverageTextColor}`}>
                          {coveragePct !== null ? `${coveragePct}% booked` : '—'}
                        </span>
                      </div>

                      {/* Booking Status summary pill */}
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap
                        ${coveragePct === null
                          ? 'border-muted text-muted-foreground bg-muted/30'
                          : coveragePct >= 95
                            ? 'border-green-200 text-green-700 bg-green-50'
                            : coveragePct >= 50
                              ? 'border-amber-200 text-amber-700 bg-amber-50'
                              : 'border-red-200 text-red-700 bg-red-50'
                        }`}>
                        {bookingHealth.statusSummary}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
